import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, BookOpenCheck, CalendarIcon, CalendarRange, ChevronLeft, ChevronRight, Download, Mic, Plus, Printer, Video, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, errText } from "@/lib/api";
import EventDialog from "@/components/EventDialog";
import { EntryActionsDialog } from "@/components/EntryActionsDialog";
import { WeekBoard } from "@/components/WeekBoard";
import { PasteMeetingDialog } from "@/components/PasteMeetingDialog";
import { ForumFilter } from "@/components/ForumFilter";
import { EdgeNav } from "@/components/EdgeNav";
import { VoiceReviewCard } from "@/components/VoiceReviewCard";
import { VoiceLogDialog } from "@/components/VoiceLogDialog";
import { downloadSheetPdf, openPrintSheet } from "@/lib/exportSheet";
import { moveEvent } from "@/lib/moveEvent";
import { addDays, isoDate, shortDate, startOfWeek, timeRange, whenLabel } from "@/lib/eventMeta";
import { alpha } from "@/lib/forumColors";
import { useAuth } from "@/context/AuthContext";
import { useForumColors } from "@/context/ForumColorContext";

const StatPill = ({ label, value, hint, testId }) => (
  <div data-testid={testId} className="surface px-4 py-3 min-w-[8.5rem] flex-1">
    <p className="label-caps">{label}</p>
    <p className="font-heading text-3xl font-bold leading-none mt-1.5">{value}</p>
    {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
  </div>
);

export default function Dashboard() {
  const { canWrite, role, workspace } = useAuth();
  const { visibleEvents, colorFor, forumName } = useForumColors();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(startOfWeek(new Date()));
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [heat, setHeat] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [voiceEvent, setVoiceEvent] = useState(null);
  const [todoCells, setTodoCells] = useState({});
  const [menuEvent, setMenuEvent] = useState(null);
  const [editing, setEditing] = useState(null);
  const [newDate, setNewDate] = useState(isoDate(new Date()));
  const [quickTitle, setQuickTitle] = useState("");
  const [quickBusy, setQuickBusy] = useState(false);
  const [clashOpen, setClashOpen] = useState(false);

  const weekStart = useMemo(() => startOfWeek(anchor), [anchor]);
  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => isoDate(addDays(weekStart, i))),
    [weekStart],
  );

  const load = useCallback(async () => {
    if (role === "clerk") return;
    try {
      const start = weekDates[0];
      const [s, ev, hm, td] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/events", { params: { start, end: weekDates[6] } }),
        api.get("/calendar/heatmap", { params: { start, end: isoDate(addDays(weekStart, 27)) } }),
        api.get("/todos/summary", { params: { start, end: weekDates[6] } }),
      ]);
      setSummary(s.data);
      setEvents(ev.data);
      setHeat(Object.fromEntries(hm.data.cells.map((c) => [c.date, c])));
      setTodoCells(Object.fromEntries(td.data.cells.map((c) => [c.date, c])));
    } catch (e) {
      toast.error(errText(e));
    }
  }, [weekDates, weekStart, workspace?.workspace_id, role]);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener("diary-refresh", handler);
    return () => window.removeEventListener("diary-refresh", handler);
  }, [load]);

  const days = useMemo(
    () =>
      weekDates.map((date) => ({
        date,
        cell: heat[date],
        events: visibleEvents(events.filter((e) => e.date === date)),
      })),
    [weekDates, events, heat, visibleEvents],
  );

  const openEvent = (event) => setMenuEvent(event);

  const editEvent = (event) => {
    setMenuEvent(null);
    setEditing(event);
    setDialogOpen(true);
  };

  const openNew = (date) => {
    setEditing(null);
    setNewDate(date);
    setDialogOpen(true);
  };

  const quickAdd = async () => {
    if (!quickTitle.trim()) return;
    setQuickBusy(true);
    try {
      const { data } = await api.post("/invites/placeholder", { text: quickTitle.trim() });
      const ev = data.event;
      setQuickTitle("");
      load();
      if (!data.found_date) {
        toast.warning("No date in that note — filed for today, tap Edit to move it");
      }
      toast.success(
        `Filed for ${shortDate(ev.date)}${ev.start_time ? ` at ${ev.start_time}` : ""}`,
        {
          description: [ev.title, data.matter_title ? `Matter: ${data.matter_title}` : null]
            .filter(Boolean)
            .join(" · "),
          duration: 10000,
          action: {
            label: "Edit",
            onClick: () => {
              setEditing(ev);
              setNewDate(ev.date);
              setDialogOpen(true);
            },
          },
          cancel: {
            label: "Undo",
            onClick: async () => {
              try {
                await api.delete(`/events/${ev.event_id}`);
                toast.success("Entry removed");
                load();
              } catch (e) {
                toast.error(errText(e));
              }
            },
          },
        },
      );
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setQuickBusy(false);
    }
  };

  if (!summary) return <div className="h-64 surface animate-pulse" data-testid="dashboard-loading" />;

  const { stats, next_up: nextUp } = summary;
  const clashRows = events
    .filter((e) => e.clash)
    .sort((a, b) => a.date.localeCompare(b.date) || (a.start_time || "").localeCompare(b.start_time || ""));
  const weekClashes = clashRows.length;
  const weekLabel = `${shortDate(weekDates[0])} — ${shortDate(weekDates[6])}`;
  const step = (dir) => setAnchor((a) => addDays(a, dir * 7));

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <EdgeNav onPrev={() => step(-1)} onNext={() => step(1)} label="week" />

      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="min-w-0">
          <p className="label-caps">{role === "read_only" ? "Read-only diary" : "This week"}</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mt-2">{weekLabel}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  data-testid="dashboard-date-picker"
                  className="rounded-full h-10 px-5 text-sm"
                >
                  <CalendarIcon className="h-4 w-4 mr-2" /> Jump to a date
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={anchor}
                  onSelect={(d) => d && setAnchor(startOfWeek(d))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <Button
              variant="outline"
              data-testid="dashboard-this-week-btn"
              onClick={() => setAnchor(startOfWeek(new Date()))}
              className="rounded-full h-10 px-4 text-sm"
            >
              This week
            </Button>
            <Button
              variant="outline"
              data-testid="dashboard-today-btn"
              onClick={() => navigate(`/calendar?view=day&date=${isoDate(new Date())}`)}
              className="rounded-full h-10 px-4 text-sm"
            >
              Today
            </Button>
            <div className="flex gap-2 md:hidden">
              <button
                data-testid="dashboard-prev-week"
                onClick={() => step(-1)}
                className="h-10 w-10 grid place-items-center border border-border rounded-sm hover:bg-accent transition-colors duration-200"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                data-testid="dashboard-next-week"
                onClick={() => step(1)}
                className="h-10 w-10 grid place-items-center border border-border rounded-sm hover:bg-accent transition-colors duration-200"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {canWrite ? (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              data-testid="dashboard-print-btn"
              onClick={() => openPrintSheet("day", isoDate(addDays(new Date(), 1)))}
              className="rounded-full h-9 px-3.5 text-sm"
            >
              <Printer className="h-4 w-4 mr-1.5" /> Tomorrow's sheet
            </Button>
            <Button
              variant="outline"
              data-testid="dashboard-pdf-btn"
              onClick={() =>
                downloadSheetPdf("week", weekDates[0]).catch((e) => toast.error(errText(e)))
              }
              className="rounded-full h-9 px-3.5 text-sm"
            >
              <Download className="h-4 w-4 mr-1.5" /> Week PDF
            </Button>
            <Button
              variant="outline"
              data-testid="dashboard-paste-meeting-btn"
              onClick={() => setPasteOpen(true)}
              className="rounded-full h-9 px-3.5 text-sm"
            >
              <Video className="h-4 w-4 mr-1.5" /> Paste a meeting
            </Button>
            <Button
              variant="outline"
              data-testid="dashboard-day-kit-btn"
              onClick={() => navigate("/kit")}
              className="rounded-full h-9 px-3.5 text-sm"
            >
              <BookOpenCheck className="h-4 w-4 mr-1.5" /> Today's kit
            </Button>
            <Button
              variant="outline"
              data-testid="dashboard-week-kit-btn"
              onClick={() => navigate(`/kit?week=${isoDate(startOfWeek(new Date()))}`)}
              className="rounded-full h-9 px-3.5 text-sm"
            >
              <CalendarRange className="h-4 w-4 mr-1.5" /> Week kit
            </Button>
            <Button
              data-testid="dashboard-new-entry-btn"
              onClick={() => openNew(isoDate(new Date()))}
              className="rounded-full h-9 px-4 text-sm"
            >
              <Plus className="h-4 w-4 mr-1.5" /> New entry
            </Button>
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-3">
        <StatPill testId="stat-today" label="Today" value={stats.today_count} hint={`${stats.today_fixed} fixed-time`} />
        <StatPill testId="stat-week" label="Next 7 days" value={stats.week_count} hint="listings" />
        <StatPill testId="stat-placeholders" label="Placeholders" value={stats.open_placeholders} hint="unassigned" />
        <StatPill testId="stat-oos" label="Travel days" value={stats.out_of_station_upcoming} hint="next 4 weeks" />
      </div>

      {weekClashes ? (
        <div
          data-testid="clash-advisory"
          className="border rounded-sm px-4 py-3"
          style={{
            borderColor: "rgb(var(--c-fixed) / 0.45)",
            background: "rgb(var(--c-fixed) / 0.08)",
          }}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="label-caps">Soft clash advisory</p>
              <p className="text-lg font-semibold">
                {weekClashes} fixed slot{weekClashes > 1 ? "s" : ""} overlap this week
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Nothing is blocked — every listing stays in your diary.
              </p>
            </div>
            <Button
              variant="outline"
              data-testid="clash-show-btn"
              onClick={() => setClashOpen((o) => !o)}
              className="rounded-full h-9 px-4 text-sm shrink-0"
            >
              <AlertTriangle className="h-4 w-4 mr-1.5" />
              {clashOpen ? "Hide the clashing matters" : "Show the clashing matters"}
            </Button>
          </div>

          {clashOpen ? (
            <div className="mt-4 space-y-2" data-testid="clash-list">
              {clashRows.map((e) => (
                <div
                  key={e.event_id}
                  data-testid={`clash-row-${e.event_id}`}
                  className="flex flex-wrap items-center gap-3 bg-background/70 border border-border rounded-sm px-3 py-2"
                >
                  <span
                    className="h-8 w-1.5 rounded-sm shrink-0"
                    style={{ background: colorFor(e) }}
                    aria-hidden
                  />
                  <button
                    onClick={() => openEvent(e)}
                    className="text-left min-w-0 flex-1 hover:underline"
                    data-testid={`clash-open-${e.event_id}`}
                  >
                    <p className="text-sm font-semibold truncate">{e.title}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {[
                        shortDate(e.date),
                        timeRange(e) || whenLabel(e),
                        forumName(e),
                        e.case_number,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </button>
                  <Button
                    variant="ghost"
                    data-testid={`clash-calendar-${e.event_id}`}
                    onClick={() => navigate(`/calendar?view=day&date=${e.date}`)}
                    className="h-8 px-3 text-xs rounded-full shrink-0"
                  >
                    On the calendar
                  </Button>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <ForumFilter />

      <WeekBoard
        days={days}
        canWrite={canWrite}
        onOpenDay={(date) => navigate(`/calendar?view=day&date=${date}`)}
        onOpenEvent={openEvent}
        onAdd={openNew}
        onMove={(event, date) => moveEvent(event, date, load)}
        todos={todoCells}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <section className="lg:col-span-7 surface p-6" data-testid="heat-strip">
          <h2 className="text-base md:text-lg font-heading font-semibold">
            Next 4 weeks — busiest forums
          </h2>
          <div className="mt-5 grid grid-cols-7 gap-1.5">
            {Array.from({ length: 28 }).map((_, i) => {
              const day = isoDate(addDays(weekStart, i));
              const cell = heat[day];
              const color = cell?.dominant_color;
              const intensity = [0, 0.22, 0.42, 0.62, 0.85][cell?.level || 0];
              return (
                <button
                  key={day}
                  data-testid={`heat-cell-${day}`}
                  onClick={() => navigate(`/calendar?date=${day}&view=day`)}
                  className="aspect-square border border-border rounded-sm hover:border-foreground transition-colors duration-200 relative"
                  style={color ? { background: alpha(color, intensity) } : undefined}
                  title={`${day} · ${cell?.total || 0} listing(s)`}
                >
                  <span className="absolute inset-0 grid place-items-center text-sm font-semibold">
                    {new Date(day).getDate()}
                  </span>
                  {cell?.full_day ? (
                    <span className="absolute top-1 left-1 h-1.5 w-1.5 rounded-full bg-foreground/70" />
                  ) : null}
                  {cell?.urgent ? (
                    <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full dot-fixed" />
                  ) : null}
                  {cell?.clashes ? (
                    <span className="absolute bottom-1 right-1 text-[0.6rem] font-bold text-fixed">!</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </section>

        <div className="lg:col-span-5 space-y-6">
          {canWrite ? <VoiceReviewCard /> : null}

          {canWrite ? (
            <section className="surface p-6" data-testid="voice-brief-card">
              <h2 className="text-base md:text-lg font-heading font-semibold inline-flex items-center gap-2">
                <Mic className="h-5 w-5" /> Voice brief for tomorrow
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Dictate the instructions once. Each matter gets its own note, and the associate brief
                goes out as a PDF or email.
              </p>
              <Button
                data-testid="dashboard-voice-brief-btn"
                onClick={() => navigate("/brief")}
                className="mt-4 rounded-full h-12 px-6"
              >
                <Mic className="h-5 w-5 mr-2" /> Record a note
              </Button>
            </section>
          ) : null}

          {canWrite ? (
            <section className="surface p-6" data-testid="quick-placeholder-panel">
              <h2 className="text-base md:text-lg font-heading font-semibold inline-flex items-center gap-2">
                <Zap className="h-5 w-5" /> Quick placeholder
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Write it the way you'd say it — "item 42 mention next Tuesday 11:30" or "Sharma
                arguments day after, afternoon". The date, time and session are read for you.
              </p>
              <div className="mt-4 flex gap-2">
                <Input
                  data-testid="quick-placeholder-input"
                  value={quickTitle}
                  onChange={(e) => setQuickTitle(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !quickBusy && quickAdd()}
                  placeholder="e.g. Item 42 mention next Tuesday 11:30"
                  className="rounded-sm h-12 text-base"
                />
                <Button
                  data-testid="quick-placeholder-btn"
                  onClick={quickAdd}
                  disabled={quickBusy}
                  className="rounded-full h-12 px-5"
                >
                  {quickBusy ? "Reading…" : "Add"}
                </Button>
              </div>
            </section>
          ) : null}

          <section className="surface p-6" data-testid="next-up-panel">
            <h2 className="text-base md:text-lg font-heading font-semibold">Coming up</h2>
            <div className="mt-4 divide-y divide-border">
              {nextUp.length ? (
                nextUp.map((e) => (
                  <button
                    key={e.event_id}
                    data-testid={`next-up-${e.event_id}`}
                    onClick={() => navigate(`/calendar?date=${e.date}&view=day`)}
                    className="w-full text-left py-3 flex items-baseline gap-4 hover:bg-accent/40 transition-colors duration-200"
                  >
                    <span className="text-sm font-semibold w-28 shrink-0">{shortDate(e.date)}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-base font-medium truncate">{e.title}</span>
                      <span className="block text-xs text-muted-foreground truncate">
                        {[e.court_name, e.case_number].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                  </button>
                ))
              ) : (
                <p className="text-sm text-muted-foreground py-3">No upcoming listings.</p>
              )}
            </div>
          </section>
        </div>
      </div>

      <EntryActionsDialog
        event={menuEvent}
        onOpenChange={(v) => !v && setMenuEvent(null)}
        onEdit={editEvent}
        onChanged={load}
      />

      <EventDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        event={editing}
        defaultDate={newDate}
        onSaved={load}
        onVoiceLog={canWrite ? setVoiceEvent : undefined}
      />

      <VoiceLogDialog
        open={Boolean(voiceEvent)}
        onOpenChange={(v) => !v && setVoiceEvent(null)}
        event={voiceEvent}
        onSaved={() => {
          load();
          window.dispatchEvent(new Event("diary-refresh"));
        }}
      />

      <PasteMeetingDialog
        open={pasteOpen}
        onOpenChange={setPasteOpen}
        onDraft={(draft) => {
          setEditing(draft);
          setNewDate(draft.date);
          setDialogOpen(true);
        }}
      />
    </div>
  );
}
